import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react';
import { MarkdownWYSIWYGEditor } from 'src/components/common/MarkdownWYSIWYGEditor';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';

export interface TaskEditState {
  taskId: string;
  title: string;
  description: string;
  metadata: Record<string, unknown> | null;
  readOnly?: boolean;
}

interface TaskEditDialogProps {
  editState: TaskEditState | null;
  onOpenChange: (open: boolean) => void;
  onTitleChange: (title: string) => void;
  onDescriptionChange: (description: string) => void;
  onSave: () => Promise<void>;
  isSaving: boolean;
}

export function TaskEditDialog({
  editState,
  onOpenChange,
  onTitleChange,
  onDescriptionChange,
  onSave,
  isSaving,
}: TaskEditDialogProps) {
  const gt = useGT();
  const [metadataOpen, setMetadataOpen] = useState(false);
  const title = editState?.title ?? '';
  const description = editState?.description ?? '';
  const readOnly = editState?.readOnly ?? false;
  const metadata = editState?.metadata;
  const agentSummary =
    typeof metadata?.agentSummary === 'string' ? metadata.agentSummary : null;
  const metadataEntries = metadata
    ? Object.entries(metadata).filter(([key]) => key !== 'agentSummary')
    : [];

  return (
    <Dialog open={!!editState} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {readOnly ? <T>View Task</T> : <T>Edit Task</T>}
          </DialogTitle>
          <DialogDescription>
            {readOnly ? (
              <T>This task is completed and cannot be edited.</T>
            ) : (
              <T>Update the task title or markdown description.</T>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="task-title">
              <T>Title</T>
            </Label>
            <Input
              id="task-title"
              value={title}
              onChange={(event) => onTitleChange(event.target.value)}
              placeholder={gt('Task title')}
              readOnly={readOnly}
              className={readOnly ? 'opacity-70' : undefined}
            />
          </div>

          <div className="space-y-2">
            <Label>
              <T>Description</T>
            </Label>
            {editState &&
              (readOnly ? (
                <div className="rounded border bg-muted/40 p-3 text-sm text-muted-foreground whitespace-pre-wrap min-h-[60px]">
                  {description || (
                    <span className="italic">
                      <T>No description</T>
                    </span>
                  )}
                </div>
              ) : (
                <MarkdownWYSIWYGEditor
                  key={editState.taskId}
                  initialValue={description}
                  onChange={onDescriptionChange}
                  placeholder={gt('Add task details')}
                  height={220}
                  showToolbar
                />
              ))}
          </div>

          {agentSummary && (
            <div className="space-y-1.5">
              <Label>
                <T>Agent Summary</T>
              </Label>
              <div className="rounded border bg-muted/40 p-3 text-sm text-muted-foreground whitespace-pre-wrap">
                {agentSummary}
              </div>
            </div>
          )}

          <div className="space-y-1">
            <button
              type="button"
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setMetadataOpen((v) => !v)}
              aria-expanded={metadataOpen}
            >
              {metadataOpen ? (
                <CaretDownIcon className="h-3 w-3" />
              ) : (
                <CaretRightIcon className="h-3 w-3" />
              )}
              <T>Metadata</T>
              {metadataEntries.length > 0 && (
                <span className="text-[10px]">({metadataEntries.length})</span>
              )}
            </button>
            {metadataOpen && (
              <div className="rounded border bg-muted/40 p-3 text-xs text-muted-foreground space-y-1">
                {metadataEntries.length === 0 ? (
                  <span className="italic">
                    <T>No metadata</T>
                  </span>
                ) : (
                  metadataEntries.map(([key, value]) => (
                    <div key={key} className="flex gap-2 min-w-0">
                      <span className="text-foreground/70 shrink-0 font-medium">
                        {key}:
                      </span>
                      <span className="break-all">
                        {typeof value === 'string'
                          ? value
                          : JSON.stringify(value, null, 0)}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          {readOnly ? (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              <T>Close</T>
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                <T>Cancel</T>
              </Button>
              <Button
                onClick={() => {
                  void onSave();
                }}
                disabled={isSaving || !title.trim()}
              >
                {isSaving ? gt('Saving...') : gt('Save changes')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
